import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n/config";

const GlobeIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3c3 3.5 3 14.5 0 18" />
    <path d="M12 3c-3 3.5-3 14.5 0 18" />
  </svg>
);

const ChevronDown = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const CheckIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const MenuIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M4 6h16" />
    <path d="M4 12h16" />
    <path d="M4 18h16" />
  </svg>
);

const XIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M18 6 6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const LANGS = [
  { code: "EN", label: "English", sub: "United States", flag: "🇺🇸" },
  { code: "TH", label: "ไทย", sub: "Thailand", flag: "🇹🇭" },
];

export default function Navbar({ onNav, lang: controlledLang, onLangChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Get current language from i18n, map to EN/TH format
  const getLangCode = (i18nLang) => {
    if (i18nLang === "th") return "TH";
    return "EN";
  };

  // Initialize language from localStorage or i18n
  useEffect(() => {
    try {
      const storedLang = localStorage.getItem("lang");
      console.log("[Navbar] Initialize - localStorage lang:", storedLang);
      console.log("[Navbar] Initialize - i18n.language:", i18n.language);
      if (storedLang && (storedLang === "TH" || storedLang === "EN")) {
        const i18nLang = storedLang === "TH" ? "th" : "en";
        console.log("[Navbar] Initialize - changing i18n to:", i18nLang);
        i18n.changeLanguage(i18nLang);
        if (!controlledLang) {
          setLang(storedLang);
        }
      }
    } catch (e) {
      console.error("[Navbar] Initialize error:", e);
    }
  }, []);

  const [lang, setLang] = useState(controlledLang ?? getLangCode(i18n.language));
  
  useEffect(() => {
    if (controlledLang) {
      setLang(controlledLang);
      // Sync with i18n
      const i18nLang = controlledLang === "TH" ? "th" : "en";
      i18n.changeLanguage(i18nLang);
    }
  }, [controlledLang]);

  // Sync i18n language changes
  useEffect(() => {
    const currentLang = getLangCode(i18n.language);
    console.log("[Navbar] Sync effect - i18n.language:", i18n.language);
    console.log("[Navbar] Sync effect - currentLang:", currentLang);
    console.log("[Navbar] Sync effect - lang state:", lang);
    console.log("[Navbar] Sync effect - controlledLang:", controlledLang);
    if (currentLang !== lang && !controlledLang) {
      console.log("[Navbar] Sync effect - updating lang state to:", currentLang);
      setLang(currentLang);
      onLangChange?.(currentLang);
    }
  }, [i18n.language, controlledLang, lang]);

  const [openLang, setOpenLang] = useState(false);
  const langWrapRef = useRef(null);
  const mobileLangDropdownRef = useRef(null);

  const [openMobile, setOpenMobile] = useState(false);
  const mobileWrapRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const current = LANGS.find((l) => l.code === lang) ?? LANGS[0];

  const goHomeTop = (e) => {
    e.preventDefault();

    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setOpenMobile(false);
      return;
    }

    navigate("/", { replace: false });
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    setOpenMobile(false);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (openMobile) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [openMobile]);

  // close dropdowns on outside click / esc
  // Use "click" (not mousedown) for lang dropdown so the option's onClick (selectLang)
  // runs first; on mousedown the dropdown could be closed before the click fires on mobile.
  // Desktop: langWrapRef. Mobile: mobileLangDropdownRef (dropdown panel) or mobileWrapRef
  // (toggle lives here; dropdown is also rendered inside it).
  useEffect(() => {
    const onDocClick = (e) => {
      const t = e.target;
      const insideLangDesktop = langWrapRef.current && langWrapRef.current.contains(t);
      const insideLangDropdown =
        mobileLangDropdownRef.current && mobileLangDropdownRef.current.contains(t);
      const insideMobileWrap = mobileWrapRef.current && mobileWrapRef.current.contains(t);
      if (!insideLangDesktop && !insideLangDropdown && !insideMobileWrap) setOpenLang(false);
    };
    const onDocMouseDown = (e) => {
      const t = e.target;
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(t) &&
        mobileWrapRef.current &&
        !mobileWrapRef.current.contains(t)
      ) {
        setOpenMobile(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpenLang(false);
        setOpenMobile(false);
      }
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // close menus when route changed
  useEffect(() => {
    setOpenMobile(false);
    setOpenLang(false);
  }, [location.pathname]);

  const selectLang = (code) => {
    console.log("[Navbar] selectLang called with code:", code);
    console.log("[Navbar] selectLang - current i18n.language:", i18n.language);
    setLang(code);
    onLangChange?.(code);
    setOpenLang(false);
    
    // Store in localStorage FIRST before changing i18n language
    // This ensures API calls will use the correct language
    try {
      localStorage.setItem("lang", code);
      console.log("[Navbar] selectLang - stored in localStorage:", code);
      const verifyLang = localStorage.getItem("lang");
      console.log("[Navbar] selectLang - verified localStorage:", verifyLang);
    } catch (e) {
      console.error("[Navbar] selectLang - localStorage error:", e);
    }
    
    // Change i18n language after localStorage is updated
    const i18nLang = code === "TH" ? "th" : "en";
    console.log("[Navbar] selectLang - converting to i18nLang:", i18nLang);
    // Change i18n language - this should trigger re-render with new language
    i18n.changeLanguage(i18nLang).then(() => {
      console.log("[Navbar] selectLang - i18n.changeLanguage completed");
      console.log("[Navbar] selectLang - new i18n.language:", i18n.language);
    });
  };

  // Active styles for router links
  const desktopLinkClass = ({ isActive }) =>
    `transition ${
      isActive ? "text-white font-semibold" : "text-white hover:text-white/60"
    } text-[15px] md:text-[16px] lg:text-[18px]`;

  const mobileLinkClass = ({ isActive }) =>
    `rounded-xl px-4 py-3 text-left font-medium transition ${
      isActive ? "bg-white/15 text-white" : "text-white hover:bg-white/10"
    } text-[15px]`;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 w-full text-white font-lexend"
      style={{ background: "linear-gradient(180deg, #1D1503 0%, #86621A 200%)" }}
    >
      {/* ✅ ลดความสูง header บนมือถือ */}
      <div className="mx-auto flex h-12 max-w-7xl items-center px-3 sm:h-14 sm:px-4 md:h-16 md:px-6 lg:px-8">
        {/* Logo */}
        <a href="/" onClick={goHomeTop} className="flex items-center" aria-label="Go to home">
          <img
            src={`${process.env.PUBLIC_URL}/assets/icons/logo.svg`}
            alt="Logo"
            className="h-6 w-auto object-contain sm:h-8 md:h-9" // ✅ มือถือเล็กลง
          />
        </a>

        {/* Desktop nav */}
        <nav className="ml-auto hidden items-center gap-6 font-lexend font-extralight lg:gap-10 md:flex">
          {onNav ? (
            <button
              type="button"
              onClick={() => onNav("home")}
              className={`transition text-[15px] md:text-[16px] lg:text-[18px] ${
                location.pathname === "/"
                  ? "text-white font-semibold"
                  : "text-white hover:text-white/60"
              }`}
            >
              {t("navbar.home")}
            </button>
          ) : (
            <NavLink to="/" className={desktopLinkClass} end>
              {t("navbar.home")}
            </NavLink>
          )}

          <NavLink to="/contact-us" className={desktopLinkClass}>
            {t("navbar.contactUs")}
          </NavLink>

          <NavLink to="/career" className={desktopLinkClass}>
            {t("navbar.career")}
          </NavLink>

          {/* Language dropdown (desktop) */}
          <div ref={langWrapRef} className="relative">
            <button
              type="button"
              onClick={() => setOpenLang((v) => !v)}
              className="
                inline-flex items-center gap-2 whitespace-nowrap
                rounded-full px-3 py-1.5
                bg-white/10 hover:bg-white/15
                border border-white/15
                shadow-[0_10px_25px_rgba(0,0,0,0.15)]
                backdrop-blur
                transition
                focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30
                font-poppins
              "
              aria-label={t("navbar.changeLanguage")}
              aria-haspopup="menu"
              aria-expanded={openLang}
            >
              <GlobeIcon className="h-5 w-5 text-white/90" />
              <span className="text-[14px] font-semibold text-white md:text-[15px]">
                {current.code}
              </span>
              <ChevronDown
                className={`h-5 w-5 text-white/90 transition-transform duration-200 ${
                  openLang ? "rotate-180" : ""
                }`}
              />
            </button>

            {openLang && (
              <div
                role="menu"
                className="
                  absolute right-0 mt-3 w-56 overflow-hidden rounded-2xl
                  bg-white/95 text-[#1f2330]
                  shadow-[0_18px_45px_rgba(0,0,0,0.25)]
                  ring-1 ring-black/10
                  backdrop-blur
                "
              >
                <div className="px-4 pt-4 pb-2">
                  <div className="text-xs font-semibold tracking-wide text-black/60">
                    {t("navbar.language")}
                  </div>
                </div>

                <div className="px-2 pb-2">
                  {LANGS.map((l) => {
                    const active = l.code === lang;
                    return (
                      <button
                        key={l.code}
                        type="button"
                        role="menuitem"
                        onClick={() => selectLang(l.code)}
                        className={`
                          w-full rounded-xl px-3 py-3 text-left
                          transition hover:bg-black/5
                          ${active ? "bg-[#B2553D]/10 ring-1 ring-[#B2553D]/25" : ""}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`
                              flex h-10 w-10 items-center justify-center rounded-full
                              ${active ? "bg-[#B2553D]/15" : "bg-black/5"}
                            `}
                          >
                            <span className="text-lg leading-none">{l.flag}</span>
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-sm font-semibold ${
                                  active ? "text-[#B2553D]" : "text-[#1f2330]"
                                }`}
                              >
                                {l.label}
                              </span>
                              <span className="text-xs text-black/45">{l.code}</span>
                            </div>
                            <div className="text-xs text-black/50">{l.sub}</div>
                          </div>

                          {active ? (
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#B2553D] text-white shadow">
                              <CheckIcon className="h-4 w-4" />
                            </div>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="h-px w-full bg-black/10" />
                <div className="px-4 py-3 text-xs text-black/50">
                  {t("navbar.tipClose")} <span className="font-semibold">Esc</span> {t("navbar.escToClose")}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile controls */}
        <div ref={mobileWrapRef} className="ml-auto flex items-center gap-2 md:hidden">
          {/* ✅ compact lang button on mobile (เล็กลง) */}
          <button
            type="button"
            onClick={() => setOpenLang((v) => !v)}
            className="
              inline-flex items-center gap-1 whitespace-nowrap
              rounded-full px-2 py-1
              bg-white/10 hover:bg-white/15
              border border-white/15
              backdrop-blur
              transition
              focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30
              font-poppins
            "
            aria-label={t("navbar.changeLanguage")}
            aria-haspopup="menu"
            aria-expanded={openLang}
          >
            <GlobeIcon className="h-4 w-4 text-white/90" />
            <span className="text-[12px] font-semibold text-white">{current.code}</span>
            <ChevronDown
              className={`h-4 w-4 text-white/90 transition-transform duration-200 ${
                openLang ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* ✅ hamburger เล็กลง */}
          <button
            type="button"
            onClick={() => setOpenMobile((v) => !v)}
            className="
              inline-flex h-8 w-8 items-center justify-center rounded-xl
              bg-white/10 hover:bg-white/15 border border-white/15
              transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30
            "
            aria-label={openMobile ? t("navbar.closeMenu") : t("navbar.openMenu")}
            aria-expanded={openMobile}
          >
            {openMobile ? <XIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
          </button>

          {/* language dropdown (mobile) */}
          {openLang && (
            <div
              ref={mobileLangDropdownRef}
              role="menu"
              className="
                fixed right-3 top-[48px] z-[60] w-56 overflow-hidden rounded-2xl
                bg-white/95 text-[#1f2330]
                shadow-[0_18px_45px_rgba(0,0,0,0.25)]
                ring-1 ring-black/10
                backdrop-blur
                sm:right-4 sm:top-[56px]
              "
            >
              <div className="px-4 pt-4 pb-2">
                <div className="text-xs font-semibold tracking-wide text-black/60">
                  {t("navbar.language")}
                </div>
              </div>
              <div className="px-2 pb-2">
                {LANGS.map((l) => {
                  const active = l.code === lang;
                  return (
                    <button
                      key={l.code}
                      type="button"
                      role="menuitem"
                      onClick={() => selectLang(l.code)}
                      className={`
                        w-full rounded-xl px-3 py-3 text-left
                        transition hover:bg-black/5
                        ${active ? "bg-[#B2553D]/10 ring-1 ring-[#B2553D]/25" : ""}
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-full ${
                            active ? "bg-[#B2553D]/15" : "bg-black/5"
                          }`}
                        >
                          <span className="text-lg leading-none">{l.flag}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-sm font-semibold ${
                                active ? "text-[#B2553D]" : "text-[#1f2330]"
                              }`}
                            >
                              {l.label}
                            </span>
                            <span className="text-xs text-black/45">{l.code}</span>
                          </div>
                          <div className="text-xs text-black/50">{l.sub}</div>
                        </div>
                        {active ? (
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#B2553D] text-white shadow">
                            <CheckIcon className="h-4 w-4" />
                          </div>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu overlay backdrop */}
      {openMobile && (
        <div
          className="fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => setOpenMobile(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile menu panel */}
      {openMobile && (
        <div
          ref={mobileMenuRef}
          className="
            fixed inset-x-0 top-[48px] z-[60] md:hidden
            max-h-[calc(100vh-48px)] overflow-y-auto
            bg-gradient-to-b from-[#1D1503] to-[#86621A]
            border-t border-white/10
            shadow-[0_10px_40px_rgba(0,0,0,0.3)]
            sm:top-[56px] sm:max-h-[calc(100vh-56px)]
          "
        >
          <div className="mx-auto max-w-7xl px-4 py-5">
            <div className="flex flex-col gap-2">
              {onNav ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      onNav("home");
                      setOpenMobile(false);
                    }}
                    className={`rounded-xl px-4 py-3 text-left font-medium transition text-[15px] ${
                      location.pathname === "/"
                        ? "bg-white/15 text-white"
                        : "text-white hover:bg-white/10"
                    }`}
                  >
                    {t("navbar.home")}
                  </button>

                  {/* Section links - only show on home page */}
                  {location.pathname === "/" && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          onNav("brand-story");
                          setOpenMobile(false);
                        }}
                        className="rounded-xl px-4 py-3 text-left font-medium transition text-[15px] text-white hover:bg-white/10"
                      >
                        {t("navbar.brandStory")}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          onNav("concept");
                          setOpenMobile(false);
                        }}
                        className="rounded-xl px-4 py-3 text-left font-medium transition text-[15px] text-white hover:bg-white/10"
                      >
                        {t("navbar.supremeConcept")}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          onNav("living");
                          setOpenMobile(false);
                        }}
                        className="rounded-xl px-4 py-3 text-left font-medium transition text-[15px] text-white hover:bg-white/10"
                      >
                        {t("navbar.livingStandards")}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          onNav("news");
                          setOpenMobile(false);
                        }}
                        className="rounded-xl px-4 py-3 text-left font-medium transition text-[15px] text-white hover:bg-white/10"
                      >
                        {t("navbar.whatsUp")}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          onNav("projects");
                          setOpenMobile(false);
                        }}
                        className="rounded-xl px-4 py-3 text-left font-medium transition text-[15px] text-white hover:bg-white/10"
                      >
                        {t("navbar.ourCollection")}
                      </button>
                    </>
                  )}
                </>
              ) : (
                <NavLink to="/" end onClick={() => setOpenMobile(false)} className={mobileLinkClass}>
                  {t("navbar.home")}
                </NavLink>
              )}

              <NavLink to="/contact-us" onClick={() => setOpenMobile(false)} className={mobileLinkClass}>
                {t("navbar.contactUs")}
              </NavLink>

              <NavLink to="/career" onClick={() => setOpenMobile(false)} className={mobileLinkClass}>
                {t("navbar.career")}
              </NavLink>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
